// ===================================
// IFRAME-OPTIMIZED PUBLIC CHAT PAGE - src/pages/PublicChatIframe.tsx
// Lean version without header/footer for embedding in iframes
// ===================================
import type React from "react";
import { useState, useEffect } from "react";
import PublicChat from "../components/PublicChat";
import { agenticAuthApi, sessionManager } from "../services/api";

import {
  Key,
  Lock,
  CheckCircle,
  AlertTriangle,
  X,
} from "lucide-react";

const PublicChatIframe: React.FC = () => {
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [lastAgenticNotice, setLastAgenticNotice] = useState<string | null>(
    null,
  );
  const [initError, setInitError] = useState<string | null>(null);

  // Get URL parameters - support both 'businessId' and 'id' for flexibility
  const params = Object.fromEntries(
    new URLSearchParams(window.location.search),
  );
  const businessId = params.businessId || params.id;

  console.log("PublicChatIframe loaded with businessId:", businessId);

  // Handle initialization errors from PublicChat
  const handleInitError = (error: string) => {
    console.error("PublicChatIframe init error:", error);
    setInitError(error);
  };

  // Agentic authentication state
  const [agenticAuthState, setAgenticAuthState] = useState({
    showModal: false,
    loading: false,
    error: "",
    code: "manifest-123456",
    lastAttempt: null as Date | null,
    isVerified: false,
  });

  // Track current session for auth purposes
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);

  // Check initial verification status
  useEffect(() => {
    const isVerified = sessionManager.isAgenticVerified();
    setAgenticAuthState((prev) => ({ ...prev, isVerified }));
  }, []);

  // Listen for chat events and agentic notices
  useEffect(() => {
    const handleAgenticNotice = (event: any) => {
      if (event.detail?.intent || event.detail?.fallback) {
        const intentInfo = event.detail.intent;

        if (intentInfo) {
          setLastAgenticNotice(`${intentInfo.action} (${intentInfo.topic})`);
        }

        setShowLoginPrompt(true);
        setAgenticAuthState((prev) => ({
          ...prev,
          showModal: true,
        }));

        if (event.detail.sessionId) {
          setCurrentSessionId(event.detail.sessionId);
        }
      }
    };

    const handleSessionCreated = (event: any) => {
      if (event.detail?.sessionId) {
        setCurrentSessionId(event.detail.sessionId);
      }
    };

    const handleVerificationChange = () => {
      const isVerified = sessionManager.isAgenticVerified();
      setAgenticAuthState((prev) => ({
        ...prev,
        isVerified,
        showModal: false,
      }));
      setShowLoginPrompt(false);
      setLastAgenticNotice(null);
    };

    window.addEventListener(
      "agenticNotice",
      handleAgenticNotice as EventListener,
    );
    window.addEventListener(
      "publicChatSessionCreated",
      handleSessionCreated as EventListener,
    );
    window.addEventListener(
      "agenticVerificationChanged",
      handleVerificationChange,
    );

    return () => {
      window.removeEventListener(
        "agenticNotice",
        handleAgenticNotice as EventListener,
      );
      window.removeEventListener(
        "publicChatSessionCreated",
        handleSessionCreated as EventListener,
      );
      window.removeEventListener(
        "agenticVerificationChanged",
        handleVerificationChange,
      );
    };
  }, []);

  const handleVerifyAccess = async () => {
    if (!agenticAuthState.code.trim()) {
      setAgenticAuthState((prev) => ({
        ...prev,
        error: "Please enter an access code",
      }));
      return;
    }

    setAgenticAuthState((prev) => ({
      ...prev,
      loading: true,
      error: "",
      lastAttempt: new Date(),
    }));

    try {
      // Use the standard verify method instead of verifyForAction
      const response = await agenticAuthApi.verify({
        accessKey: agenticAuthState.code.trim(),
      });

      if (response.data.ok) {
        sessionManager.setAgenticVerified(response.data.principalId);

        setAgenticAuthState({
          showModal: false,
          loading: false,
          error: "",
          code: "manifest-123456",
          lastAttempt: null,
          isVerified: true,
        });

        setShowLoginPrompt(false);
        setLastAgenticNotice(null);

        const successEvent = new CustomEvent("agenticVerificationChanged");
        window.dispatchEvent(successEvent);

        if (response.data.replay || response.data.replay_response) {
          setTimeout(() => {
            const replayEvent = new CustomEvent("agenticActionReplayed", {
              detail: {
                response: response.data.replay_response || response.data.replay,
              },
            });
            window.dispatchEvent(replayEvent);

            const executedEvent = new CustomEvent("showNotification", {
              detail: {
                type: "success",
                title: "Action Executed",
                message: "Your request has been processed automatically.",
              },
            });
            window.dispatchEvent(executedEvent);
          }, 100);
        }
      } else {
        setAgenticAuthState((prev) => ({
          ...prev,
          loading: false,
          error:
            response.data.message ||
            "Invalid access code. Please try again by calling the previous action and then enter a valid verification code.",
        }));
      }
    } catch (error) {
      setAgenticAuthState((prev) => ({
        ...prev,
        loading: false,
        error: "Verification failed. Please try again.",
      }));
    }
  };

  return (
    <div className="h-screen bg-gradient-to-br from-[#0a0f1e] via-[#0f1428] to-[#1a1f3a] flex flex-col overflow-hidden">
      {/* Error State - Show user-friendly error instead of redirecting */}
      {initError && (
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-2xl mb-4">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Unable to Load Chat
            </h2>
            <p className="text-gray-400 mb-6">{initError}</p>
            <button
              onClick={() => {
                setInitError(null);
                window.location.reload();
              }}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium rounded-lg transition-all"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Lean Chat Container - No Header/Footer */}
      {!initError && (
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Chat Interface - Takes full available height */}
          <div className="flex-1 w-full max-w-4xl mx-auto px-4 py-4 overflow-hidden min-h-0">
            <PublicChat
              isEmbedded={true}
              className="h-full rounded-xl border border-white/10 shadow-2xl"
              businessId={businessId}
              onInitError={handleInitError}
            />
          </div>
        </div>
      )}

      {/* Agentic Authentication Modal */}
      {agenticAuthState.showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-[#1a1f3a] to-[#0f1428] rounded-2xl shadow-2xl w-full max-w-md border border-white/10 relative">
            {/* Close button */}
            <button
              onClick={() =>
                setAgenticAuthState((prev) => ({ ...prev, showModal: false }))
              }
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
              disabled={agenticAuthState.loading}
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="p-8 pb-6">
              <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl mb-6 mx-auto">
                <Lock className="w-8 h-8 text-white" />
              </div>

              <h2 className="text-2xl font-bold text-white text-center mb-2">
                Enhanced Access Required
              </h2>
              <p className="text-gray-400 text-center text-sm">
                {lastAgenticNotice ? (
                  <>
                    To perform{" "}
                    <strong className="text-purple-400">
                      {lastAgenticNotice}
                    </strong>
                    , please verify your identity
                  </>
                ) : (
                  "Please enter your access code to continue"
                )}
              </p>
            </div>

            {/* Body */}
            <div className="px-8 pb-8 space-y-6">
              {/* Access Code Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Access Code
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="text"
                    value={agenticAuthState.code}
                    onChange={(e) =>
                      setAgenticAuthState((prev) => ({
                        ...prev,
                        code: e.target.value,
                        error: "",
                      }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !agenticAuthState.loading) {
                        handleVerifyAccess();
                      }
                    }}
                    placeholder="Enter your access code"
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    disabled={agenticAuthState.loading}
                  />
                </div>
                {agenticAuthState.error && (
                  <div className="mt-2 flex items-start space-x-2 text-red-400 text-sm">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{agenticAuthState.error}</span>
                  </div>
                )}
              </div>

              {/* Verify Button */}
              <button
                onClick={handleVerifyAccess}
                disabled={agenticAuthState.loading}
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
              >
                {agenticAuthState.loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Verify Access</span>
                  </>
                )}
              </button>

              {/* Info Box */}
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-white text-xs font-bold">i</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-blue-200">
                      Your access code verifies your identity and enables
                      enhanced features
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicChatIframe;
